import type { Part } from '../../domain/inventory';
import {
  validateRequirement,
  type FulfillmentRequirement,
  type RequirementType,
} from '../../domain/fulfillment';
import { humanizeIdentifier } from '../../lib/formatting';
import type { LineChoice } from './line-picker-entries';
import { partChoiceOf, partIdsForChoice } from './part-choice';

export interface RequirementRow {
  key: string;
  requirement: FulfillmentRequirement;
  locationId?: string | undefined;
}

let rowCounter = 0;
export function newRequirementRow(
  type: RequirementType,
  requirement?: Partial<FulfillmentRequirement>,
  locationId?: string,
): RequirementRow {
  rowCounter += 1;
  return {
    key: `row-${rowCounter}`,
    requirement: { type, partIds: [], quantity: 1, ...requirement },
    locationId,
  };
}

/** What a line is for, as the picker shows it; undefined for none or several part types. */
export function lineChoiceOf(
  requirement: FulfillmentRequirement,
  parts: readonly Part[],
): LineChoice | undefined {
  if (requirement.type === 'STOCK') {
    return requirement.stockItemId
      ? { kind: 'stock', stockItemId: requirement.stockItemId }
      : undefined;
  }
  const choice = partChoiceOf(requirement.partIds, parts);
  return choice.type ? { kind: 'serialized', partType: choice.type } : undefined;
}

/**
 * A line for what was chosen: a part type takes any build until narrowed,
 * and is named after the type unless someone named the line themselves.
 * The quantity and a name someone gave carry over when a line switches.
 */
export function requirementForChoice(
  current: FulfillmentRequirement,
  choice: LineChoice,
  parts: readonly Part[],
): FulfillmentRequirement {
  const previous = lineChoiceOf(current, parts);
  const label = current.label?.trim();
  // A label that is just the previous part type's name was given for it.
  const ownLabel =
    label && !(previous?.kind === 'serialized' && label === humanizeIdentifier(previous.partType))
      ? current.label
      : undefined;
  if (choice.kind === 'stock') {
    return {
      ...current,
      type: 'STOCK',
      partIds: [],
      stockItemId: choice.stockItemId,
      label: ownLabel,
    };
  }
  return {
    ...current,
    type: 'SERIALIZED',
    partIds: partIdsForChoice(parts, choice.partType, []),
    stockItemId: undefined,
    label: ownLabel ?? humanizeIdentifier(choice.partType),
  };
}

/** The first problem with any row, for a form-level message. */
export function requirementRowsError(rows: RequirementRow[]): string | null {
  for (const [index, row] of rows.entries()) {
    const problem = validateRequirement(row.requirement);
    if (problem) return `Line ${index + 1}: ${problem}`;
  }
  return null;
}
